import { HandledError, PRODUCT_NAME } from "@amtana/module";

import { erasureBlockedDetailsSchema } from "./auth.schemas.ts";

export const AUTH_ERROR_CODES = {
  invalidCredentials: "auth_invalid_credentials",
  emailTaken: "auth_email_taken",
  throwawayEmail: "auth_throwaway_email",
  challengeFailed: "auth_challenge_failed",
  ssoRequired: "auth_sso_required",
  ssoNotAdmitted: "auth_sso_not_admitted",
  verificationInvalid: "auth_verification_invalid",
  verificationExpired: "auth_verification_expired",
  verificationRateLimited: "auth_verification_rate_limited",
  passwordResetInvalid: "auth_password_reset_invalid",
  passwordResetExpired: "auth_password_reset_expired",
  currentPasswordInvalid: "auth_current_password_invalid",
  sessionNotFound: "auth_session_not_found",
  passkeyChallengeInvalid: "auth_passkey_challenge_invalid",
  passkeyChallengeExpired: "auth_passkey_challenge_expired",
  passkeyVerificationFailed: "auth_passkey_verification_failed",
  passkeyCounterReused: "auth_passkey_counter_reused",
  passkeyNotFound: "auth_passkey_not_found",
  emailChangeInvalid: "auth_email_change_invalid",
  emailChangeExpired: "auth_email_change_expired",
  googleSignInInvalid: "auth_google_sign_in_invalid",
  googleSignInFailed: "auth_google_sign_in_failed",
  googleEmailUnverified: "auth_google_email_unverified",
  googleAccountTaken: "auth_google_account_taken",
  linkedinSignInInvalid: "auth_linkedin_sign_in_invalid",
  linkedinSignInFailed: "auth_linkedin_sign_in_failed",
  linkedinEmailUnverified: "auth_linkedin_email_unverified",
  linkedinAccountTaken: "auth_linkedin_account_taken",
  oidcIssuerRefused: "auth_oidc_issuer_refused",
  erasureBlocked: "auth_erasure_blocked",
  erasureConfirmationMismatch: "auth_erasure_confirmation_mismatch",
} as const;

// A sign-in through Google or LinkedIn (ADR-007) refuses with the provider's own code, so the
// person reads which provider it was about.
const OIDC_CODES = {
  google: {
    invalid: AUTH_ERROR_CODES.googleSignInInvalid,
    failed: AUTH_ERROR_CODES.googleSignInFailed,
    unverified: AUTH_ERROR_CODES.googleEmailUnverified,
    taken: AUTH_ERROR_CODES.googleAccountTaken,
  },
  linkedin: {
    invalid: AUTH_ERROR_CODES.linkedinSignInInvalid,
    failed: AUTH_ERROR_CODES.linkedinSignInFailed,
    unverified: AUTH_ERROR_CODES.linkedinEmailUnverified,
    taken: AUTH_ERROR_CODES.linkedinAccountTaken,
  },
} as const;
type OidcProviderId = keyof typeof OIDC_CODES;
const OIDC_LABELS: Readonly<Record<OidcProviderId, string>> = { google: "Google", linkedin: "LinkedIn" };

export class InvalidCredentialsError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.invalidCredentials,
      status: 401,
      message: "Incorrect email or password.",
    });
  }
}

export class EmailTakenError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.emailTaken,
      status: 409,
      message: "An account with this email already exists.",
    });
  }
}

/** ADR-025 §1: the address is on a throwaway service. The answer never says it is on a list. */
export class ThrowawayEmailError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.throwawayEmail,
      status: 422,
      message: "Use a permanent email address.",
    });
  }
}

/** ADR-025 §8: the sign-up form's challenge was missing or did not pass. */
export class SignUpChallengeFailedError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.challengeFailed,
      status: 400,
      message: "The sign-up check did not pass.",
    });
  }
}

export class SsoRequiredError extends HandledError {
  constructor(providerName: string) {
    super({
      code: AUTH_ERROR_CODES.ssoRequired,
      status: 403,
      message: `Your company signs in with ${providerName}. Continue with single sign-on.`,
    });
  }
}

export class SsoNotAdmittedError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.ssoNotAdmitted,
      status: 403,
      message: `Your company has not opened ${PRODUCT_NAME} to new people. Ask your admin to invite you.`,
    });
  }
}

export class VerificationTokenInvalidError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.verificationInvalid,
      status: 400,
      message: "This verification link is not valid. It may already have been used.",
    });
  }
}

export class VerificationTokenExpiredError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.verificationExpired,
      status: 400,
      message: "This verification link has expired.",
    });
  }
}

export class VerificationRateLimitedError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.verificationRateLimited,
      status: 429,
      message: "A verification link was already sent. Wait a bit before requesting another.",
    });
  }
}

export class PasswordResetTokenInvalidError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.passwordResetInvalid,
      status: 400,
      message: "This password reset link is not valid. It may already have been used.",
    });
  }
}

export class PasswordResetTokenExpiredError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.passwordResetExpired,
      status: 400,
      message: "This password reset link has expired.",
    });
  }
}

export class CurrentPasswordInvalidError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.currentPasswordInvalid,
      status: 401,
      message: "Your current password is incorrect.",
    });
  }
}

export class SessionNotFoundError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.sessionNotFound,
      status: 404,
      message: "That session no longer exists.",
    });
  }
}

export class PasskeyChallengeInvalidError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.passkeyChallengeInvalid,
      status: 400,
      message: "This passkey ceremony is not valid. Please try again.",
    });
  }
}

export class PasskeyChallengeExpiredError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.passkeyChallengeExpired,
      status: 400,
      message: "This passkey ceremony took too long. Please try again.",
    });
  }
}

export class PasskeyVerificationFailedError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.passkeyVerificationFailed,
      status: 400,
      message: "This passkey could not be verified.",
    });
  }
}

/** A refused ceremony whose authenticator reported a signature counter that did not advance:
 * the strongest signal a passkey's private key has been cloned (ADR-004 point 4). */
export class PasskeyCounterReusedError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.passkeyCounterReused,
      status: 401,
      message: "This passkey looks like it was copied to another device. Please add it again.",
    });
  }
}

export class PasskeyNotFoundError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.passkeyNotFound,
      status: 404,
      message: "That passkey was not found.",
    });
  }
}

export class EmailChangeTokenInvalidError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.emailChangeInvalid,
      status: 400,
      message: "This confirmation link is not valid. It may already have been used.",
    });
  }
}

export class EmailChangeTokenExpiredError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.emailChangeExpired,
      status: 400,
      message: "This confirmation link has expired.",
    });
  }
}

/** Why `OidcSignInInvalidError` refused (D948, for the logs only, `meta.reason`): a state this
 * server never issued or already spent, one started for another provider, one started more
 * than ten minutes ago, or an ID token minted for another sign-in. */
export const OIDC_SIGN_IN_INVALID_REASONS = [
  "unknown_or_used_state",
  "provider_mismatch",
  "expired",
  "nonce_mismatch",
] as const;
export type OidcSignInInvalidReason = (typeof OIDC_SIGN_IN_INVALID_REASONS)[number];

/** A return from a provider this server never started, already used, older than ten minutes,
 * or carrying a token minted for another sign-in. */
export class OidcSignInInvalidError extends HandledError {
  constructor(provider: OidcProviderId, reason: OidcSignInInvalidReason) {
    super({
      code: OIDC_CODES[provider].invalid,
      status: 400,
      message: `This ${OIDC_LABELS[provider]} sign-in has expired or was already used. Please try again.`,
      meta: { reason },
    });
  }
}

/** The provider refused the code, could not be reached, or sent an ID token that does not check out. */
export class OidcSignInFailedError extends HandledError {
  constructor(provider: OidcProviderId) {
    super({
      code: OIDC_CODES[provider].failed,
      status: 503,
      transient: {},
      message: `${OIDC_LABELS[provider]} could not confirm who you are. Please try again.`,
    });
  }
}

export class OidcEmailUnverifiedError extends HandledError {
  constructor(provider: OidcProviderId) {
    super({
      code: OIDC_CODES[provider].unverified,
      status: 403,
      message: `${OIDC_LABELS[provider]} has not verified this email address. Sign in another way.`,
    });
  }
}

export class OidcAccountTakenError extends HandledError {
  constructor(provider: OidcProviderId) {
    super({
      code: OIDC_CODES[provider].taken,
      status: 409,
      message: `This ${OIDC_LABELS[provider]} account is already connected to another ${PRODUCT_NAME} account.`,
    });
  }
}

/** Staff set an issuer production must never use: a loopback host is a simulator (ADR-008). */
export class OidcIssuerRefusedError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.oidcIssuerRefused,
      status: 422,
      message: "That issuer is a loopback host, a simulator; production signs in through the real provider.",
      fault: "customer",
    });
  }
}

/** Self-service erasure's own gate refused (ADR-026 point 2, epic 30, S30.01): the person
 * administers at least one shared organization alone with others, a question an unattended
 * door cannot decide on someone else's behalf. Names every organization that blocks it, so
 * S30.02's own screen can point at each one without asking again. */
export class ErasureBlockedError extends HandledError {
  constructor(organizations: readonly { readonly organizationId: string; readonly name: string }[]) {
    super({
      code: AUTH_ERROR_CODES.erasureBlocked,
      status: 409,
      message: `Remove the other members, transfer the role, or leave ${joinNames(
        organizations.map((organization) => organization.name),
      )} first.`,
      // S30.02; revised by review group 3 security finding 7 (D898+), replacing D863's blanket
      // meta-over-the-wire with this opt-in `details`, validated here against the same zod
      // schema the screen reads it back through (`erasureBlockedDetailsSchema`): the id and
      // name travel over the wire (`wireErrorOf`, a customer-fault status) structured, never
      // the message above, which is for the logs only.
      details: erasureBlockedDetailsSchema.parse({ organizations }),
    });
  }
}

/** Self-erase's typed confirmation, checked server-side (review group 3 security finding 8):
 * the Danger Zone already refuses client-side to even call `deleteAccount` until the person
 * types their own email (`erasureConfirmationMatches`, `modules/account/browser`), but a
 * client-side-only check is not a check at all once the door is reachable directly — the same
 * reasoning `/ops`'s own `OpsPersonErasureConfirmationMismatchError` already applies to staff
 * erasure (D866). */
export class ErasureConfirmationMismatchError extends HandledError {
  constructor() {
    super({
      code: AUTH_ERROR_CODES.erasureConfirmationMismatch,
      status: 400,
      message: "Type your own email address exactly to confirm.",
    });
  }
}

/** "Acme", "Acme and Globex", or "Acme, Globex and Initech" — never an Oxford comma, matching
 * this codebase's own prose voice elsewhere. */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
